import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useThemeMode } from '../context/ThemeContext';

const NAV = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'skills', label: 'Skills' },
  { id: 'services', label: 'Services' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'contact', label: 'Contact' },
];

const Bar = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transition: background 250ms ease, backdrop-filter 250ms ease, border-color 250ms ease;

  ${({ $scrolled, theme }) =>
    $scrolled &&
    css`
      background: ${theme.colors.overlay};
      backdrop-filter: saturate(160%) blur(14px);
      border-bottom: 1px solid ${theme.colors.border};
    `}
`;

const Wrap = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.a`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 700;
  font-size: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  span {
    color: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Nav = styled.nav`
  display: none;
  gap: 6px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const NavLink = styled.a`
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radii.pill};
  font-size: 14px;
  color: ${({ theme, $active }) => ($active ? theme.colors.text : theme.colors.textDim)};
  background: ${({ theme, $active }) => ($active ? theme.colors.surfaceHi : 'transparent')};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.border : 'transparent')};
  cursor: pointer;
  transition: color 200ms ease, background 200ms ease, transform 200ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    transform: translateY(-1px);
  }
`;

const MobileBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const ThemeToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: color 200ms ease, border-color 200ms ease, transform 200ms ease, background 200ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primarySoft};
    transform: translateY(-1px);
  }
`;

const RightSide = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 72px 16px auto 16px;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px;
  z-index: 49;
  display: grid;
  gap: 4px;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileLink = styled.a`
  padding: 12px 14px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.textDim};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceHi};
  }
`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('home');
  const { mode, toggle } = useThemeMode();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      // Determine active section
      const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
      const y = window.scrollY + 140;
      let current = 'home';
      sections.forEach((s) => {
        if (s.offsetTop <= y) current = s.id;
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Bar
      $scrolled={scrolled}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <Wrap>
        <Logo onClick={go('home')} href="#home">
          Darryl<span>.</span>
        </Logo>
        <Nav>
          {NAV.map((n) => (
            <NavLink key={n.id} href={`#${n.id}`} onClick={go(n.id)} $active={active === n.id}>
              {n.label}
            </NavLink>
          ))}
        </Nav>
        <RightSide>
          <ThemeToggle onClick={toggle} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mode}
                initial={{ y: -14, opacity: 0, rotate: -90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 14, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.25 }}
                style={{ display: 'inline-flex' }}
              >
                {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </motion.span>
            </AnimatePresence>
          </ThemeToggle>
          <MobileBtn aria-label="Toggle menu" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </MobileBtn>
        </RightSide>
      </Wrap>

      <AnimatePresence>
        {open && (
          <MobileMenu
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {NAV.map((n) => (
              <MobileLink key={n.id} href={`#${n.id}`} onClick={go(n.id)}>
                {n.label}
              </MobileLink>
            ))}
          </MobileMenu>
        )}
      </AnimatePresence>
    </Bar>
  );
}
